import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { Logo, PrimaryButton } from 'components/index';
import { selectCurrentUser, fetchCurrentUser } from 'ducks/user.duck';
import { manageDisableScrolling } from 'ducks/ui.duck';
import { updateUserProfile } from '../../../profile-slug/utils/api';
import { usePostHog } from '../../../../hooks/usePostHogSafe';

import css from './WelcomeModal.module.css';

const STEPS = [
  {
    title: (name) => `Welcome, ${name.charAt(0).toUpperCase()}${name.slice(1)}!`,
    body: "We're the online marketplace for children's classes, clubs and camps. Thousands of parents use Classhoppa to discover and book activities for their kids — and you're about to become part of that.",
    cta: 'Next',
  },
  {
    title: () => "Let's get started",
    body: "List your first class so parents can find and book with you. It only takes a few minutes.",
    cta: 'List your first class',
  },
];

const TOTAL_STEPS = STEPS.length;

const WelcomeScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentUser = useSelector(selectCurrentUser);

  const onboardingPosition = currentUser?.attributes.profile.publicData?.onboardingPosition;
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const posthog = usePostHog();

  useEffect(() => {
    if (onboardingPosition === 'welcome') {
      setIsOpen(true);
      posthog?.capture?.('onboarding welcome modal shown');
    } else {
      setIsOpen(false);
    }
  }, [onboardingPosition]);

  useEffect(() => {
    if (onboardingPosition !== 'welcome') return;
    if (pathname === '/dashboard') {
      updateUserProfile({ publicData: { onboardingPosition: 'list-a-class' } })
        .then(() => {
          posthog?.capture?.('onboarding transition list-a-class');
          dispatch(fetchCurrentUser({ enforce: true }));
        });
    }
  }, [pathname]);

  useEffect(() => {
    dispatch(manageDisableScrolling('WelcomeScreen', isOpen));
    return () => dispatch(manageDisableScrolling('WelcomeScreen', false));
  }, [isOpen, dispatch]);

  const handleClose = () => {
    posthog?.capture?.('onboarding welcome modal skipped');
    navigate('/dashboard');
  };

  const handleCta = async () => {
    if (step < TOTAL_STEPS - 1) {
      posthog?.capture?.('onboarding welcome modal next clicked');
      setStep((s) => s + 1);
    } else {
      posthog?.capture?.('onboarding welcome modal list class clicked');
      setIsOpen(false);
      await updateUserProfile({ publicData: { onboardingPosition: 'list-a-class' } });
      posthog?.capture?.('onboarding transition list-a-class');
      navigate('/book/draft/00000000-0000-0000-0000-000000000000/new/details?first-time=true');
    }
  };

  if (!isOpen) return null;

  const { title, body, cta } = STEPS[step];
  const firstName = currentUser?.attributes.profile.firstName;

  return (
    <div className={css.overlay}>
      <div className={css.card}>
        <div className={css.logoTop}>
          <Logo layout="desktop" className={css.logoTopImg} />
        </div>

        <div key={step} className={css.content}>
          <h1 className={css.title}>{title(firstName)}</h1>
          <p className={css.body}>{body}</p>

          <PrimaryButton className={css.cta} onClick={handleCta}>
            {cta}
          </PrimaryButton>

          <button className={css.skip} onClick={handleClose}>
            Skip for now
          </button>
        </div>

        <div className={css.dots}>
          {STEPS.map((_, i) => (
            <span key={i} className={classNames(css.dot, { [css.dotActive]: i === step })} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
